import { IconButton } from "@dropbox/dig-components/buttons";
import { ClickOutside } from "@dropbox/dig-components/click_outside";
import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine, OpenLine, SearchLine } from "@dropbox/dig-icons/assets";
import { PAP_Click_AddSource } from "@mirage/analytics/events/types/click_add_source";
import useConnectors from "@mirage/service-connectors/useConnectors";
import { buildSummarizable } from "@mirage/service-dbx-api/service/utils";
import { openURL } from "@mirage/service-platform-actions";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { SearchFilterType } from "@mirage/shared/search/search-filters";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { memo, useCallback, useEffect, useMemo } from "react";
import { ComposeSourceRow } from "./ComposeSourceRow";
import { FilterChip } from "./FilterChip";
import { SourcesFilter } from "./SourcesFilter";
import styles from "./SourcesSearch.module.css";
import { useSourcesFilter } from "./useSourcesFilter";
import { isSearchResultSupported, useSourcesSearch } from "./useSourcesSearch";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { ComposeSourceRowProps } from "./ComposeSourceRow";

interface SourceSearchProps {
  aboveSearchResultsLabel?: string;
  aboveSearchResults?: React.ReactNode;
  existingSources: ChatSource[];
  onAddSource: (source: ChatSource) => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  hasBorder?: boolean;
  hideSearchResultsLabel?: boolean;
  uploadButton?: React.ReactNode;
  enableFilters?: boolean;
  closeResultsOnDragDrop?: boolean;
}

export const SourcesSearch = memo(
  ({
    aboveSearchResultsLabel,
    aboveSearchResults,
    existingSources,
    onAddSource,
    logComposeEvent,
    hasBorder,
    hideSearchResultsLabel,
    uploadButton,
    enableFilters = false,
    closeResultsOnDragDrop,
  }: SourceSearchProps) => {
    const { connectors } = useConnectors();

    const {
      selectedAppTypes,
      handleAppTypeSelect,
      handleFilterMenuOpen,
      matchesFilter,
    } = useSourcesFilter({
      onFilterChange: () => {
        // Trigger search when filter changes to get results matching new filter
        triggerSearch();
      },
      onMenuOpen: (isOpen) => {
        // Close search results when filter menu opens
        if (isOpen) {
          setIsSearchOpen(false);
        }
      },
    });

    const activeFilters: SearchFilter[] = useMemo(() => {
      return Array.from(selectedAppTypes).map((appType) => {
        const connector = connectors.find((c) => c.id_attrs?.type === appType);
        return {
          id: appType,
          type: SearchFilterType.Connector,
          parameters: {
            connectorId: appType,
            displayName: connector?.branding?.display_name || appType,
          },
        };
      });
    }, [selectedAppTypes, connectors]);

    const {
      searchState,
      isSearchOpen,
      containerWidth,
      inputRef,
      containerRef,
      searchResultsRef,
      handleChangeQuery,
      handleInputClick,
      handleClearSearch,
      handleClickOutside,
      handleContainerClick,
      setIsSearchOpen,
      getFilteredSources,
      triggerSearch,
      isLoadingEmptyQueryWithFilters,
    } = useSourcesSearch({
      existingSources,
      logComposeEvent,
      matchesFilter,
      activeFilters,
    });

    // Close search results when drag and drop upload completes
    useEffect(() => {
      if (closeResultsOnDragDrop) {
        setIsSearchOpen(false);
      }
    }, [closeResultsOnDragDrop, setIsSearchOpen]);

    const handleOpenLine = useCallback(
      (url: string) => {
        if (url) {
          openURL(url);
          inputRef.current?.focus();
        }
      },
      [inputRef],
    );

    const handleAddSource = useCallback(
      (source: ChatSource) => {
        // Don't add unusable sources
        if (
          source.type === "dash_search_result" &&
          !isSearchResultSupported(source.searchResult)
        ) {
          return;
        }

        logComposeEvent(
          PAP_Click_AddSource({
            actionType: "search",
          }),
        );
        onAddSource(source);
        setIsSearchOpen(false);
      },
      [logComposeEvent, onAddSource, setIsSearchOpen],
    );

    const getSourceAction = useCallback(
      (source: ChatSource): ComposeSourceRowProps["action"] => {
        if (
          source.type === "dash_search_result" &&
          buildSummarizable(source.searchResult.summarizable) !==
            "yes_summarizable"
        ) {
          return undefined;
        }

        const url =
          source.type === "dash_search_result"
            ? source.searchResult.url
            : source.type === "recommendation"
              ? source.recommendation.url
              : "";

        if (
          selectedAppTypes.size > 0 ||
          source.type === "recommendation" ||
          url
        ) {
          return {
            icon: <UIIcon src={OpenLine} />,
            variant: "default" as const,
            onClick: () => handleOpenLine(url ?? ""),
            label: i18n.t("compose_source_row_open_button_label"),
            disabled: !url,
          };
        }
        return undefined;
      },
      [selectedAppTypes.size, handleOpenLine],
    );

    const renderSearchResults = () => {
      // Don't render search results if filter menu is open
      if (!isSearchOpen) return null;

      const shouldShowLoading =
        (searchState.currentQuery.length > 0 &&
          searchState.currentQuery !== searchState.lastSearch?.query) ||
        isLoadingEmptyQueryWithFilters;

      if (shouldShowLoading) {
        return (
          <Box
            ref={searchResultsRef}
            className={styles.searchResultsContainer}
            style={{ width: containerWidth, overflow: "hidden" }}
            onClick={handleContainerClick}
          >
            <div className={styles.searchResultsLoading}>
              <Spinner size="large" aria-valuetext={i18n.t("loading")} />
            </div>
          </Box>
        );
      }

      const displaySources = getFilteredSources(selectedAppTypes);

      return (
        <Box
          ref={searchResultsRef}
          className={styles.searchResultsContainer}
          style={{ width: containerWidth }}
          onClick={handleContainerClick}
        >
          {aboveSearchResultsLabel && (
            <Text
              color="subtle"
              size="small"
              tagName="div"
              className={styles.SourcesSectionLabelAbove}
            >
              {aboveSearchResultsLabel}
            </Text>
          )}
          {aboveSearchResults}
          {!hideSearchResultsLabel && (
            <div className={styles.searchResultsHeader}>
              <Text variant="label" color="subtle" size="small" isBold>
                {searchState.currentQuery.length > 0
                  ? i18n.t("compose_search_results_title")
                  : i18n.t("compose_recommendations_title")}
              </Text>
            </div>
          )}
          <div className={styles.searchResultsContent}>
            {displaySources.length > 0 ? (
              <div className={styles.SourceSearchResultRows}>
                {displaySources.map((source, i) => (
                  <ComposeSourceRow
                    key={`${source.type}-${getSourceUUID(source) || i}`}
                    source={source}
                    action={getSourceAction(source)}
                    variant={hasBorder ? "small" : "default"}
                    config={{ hideBackground: true }}
                    handleAddSource={handleAddSource}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.SourcesEmpty}>
                <Text size="medium" color="subtle" tagName="div">
                  {i18n.t(
                    searchState.currentQuery.length === 0
                      ? "compose_sources_no_results_empty_query_description"
                      : "compose_sources_no_results_description",
                  )}
                </Text>
              </div>
            )}
          </div>
        </Box>
      );
    };

    return (
      <div
        className={classnames(styles.SourcesSearchContainer, {
          [styles.SourcesSearchContainerBordered]: hasBorder,
        })}
      >
        <ClickOutside
          isActive={isSearchOpen}
          onClickOutside={handleClickOutside}
          isBlock={false}
          isClickThroughPortaled={true}
          shouldPropagateMouseEvents={true}
        >
          <div
            className={classnames(styles.SourcesSearchInput, {
              [styles.SourcesSearchInputBorder]: isSearchOpen,
            })}
          >
            <div ref={containerRef} className={styles.typeaheadInputContainer}>
              <TextInput.Container size="large" onClick={handleInputClick}>
                <TextInput.Accessory position="start">
                  <UIIcon
                    src={SearchLine}
                    size="medium"
                    className={styles.searchIcon}
                  />
                </TextInput.Accessory>
                <TextInput.ChipsContainer>
                  {enableFilters && (
                    <FilterChip
                      selectedAppTypes={selectedAppTypes}
                      onDeselectAll={() => {
                        handleAppTypeSelect(new Set());
                        setIsSearchOpen(false);
                      }}
                    />
                  )}
                  <TextInput.Input
                    className={styles.SourcesSearchInputField}
                    placeholder={i18n.t(
                      "compose_sources_search_input_placeholder",
                    )}
                    size={12}
                    onChange={handleChangeQuery}
                    ref={inputRef}
                  />
                </TextInput.ChipsContainer>
                {searchState.currentQuery.length > 0 && (
                  <DigTooltip
                    title={i18n.t("clear_search")}
                    placement="bottom-end"
                    isPortaled={true}
                  >
                    <IconButton
                      aria-label={i18n.t("clear_search")}
                      variant="borderless"
                      className={styles.SourcesSearchClearButton}
                      onClick={handleClearSearch}
                      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                      }}
                      type="reset"
                      style={{ borderRadius: "4px" }}
                    >
                      <UIIcon src={CloseLine} size="medium" />
                    </IconButton>
                  </DigTooltip>
                )}
                {enableFilters && (
                  <SourcesFilter
                    onFilterChange={handleAppTypeSelect}
                    selectedAppTypes={selectedAppTypes}
                    onMenuOpen={handleFilterMenuOpen}
                  />
                )}
              </TextInput.Container>
            </div>
            {uploadButton}
          </div>
          {renderSearchResults()}
        </ClickOutside>
      </div>
    );
  },
);
SourcesSearch.displayName = "SourcesSearch";
